import { client } from "../../utils/kibelaClient2";

import type { GetNotificationsQuery, GetUnreadNotificationsCountQuery } from "~/client/generated/schema";
import {
  GetNotificationsDocument,
  GetUnreadNotificationsCountDocument,
  MarkNotificationsAsReadDocument,
} from "~/client/generated/schema";

interface Props {
  unreadCount: number;
  notifications: GetNotificationsQuery["notifications"];
  isNotificationsLoaded: boolean;
  isError: boolean;
}

// this class is lens
export class NotificationModel {
  public readonly unreadCount: number;
  public readonly notifications: GetNotificationsQuery["notifications"];
  public readonly isNotificationsLoaded: boolean;
  public readonly isError: boolean;

  readonly defaultProps = {
    unreadCount: 0,
    notifications: { nodes: [] },
    isNotificationsLoaded: false,
    isError: false,
  } as Props;

  constructor(props?: Props) {
    this.unreadCount = props ? props.unreadCount : this.defaultProps.unreadCount;
    this.notifications = props ? props.notifications : this.defaultProps.notifications;
    this.isNotificationsLoaded = props ? props.isNotificationsLoaded : this.defaultProps.isNotificationsLoaded;
    this.isError = props ? props.isError : this.defaultProps.isError;
  }

  copy(newProps: {
    unreadCount?: number;
    notifications?: GetNotificationsQuery["notifications"];
    isNotificationsLoaded?: boolean;
    isError?: boolean;
  }) {
    const oldProps = {
      unreadCount: this.unreadCount,
      notifications: this.notifications,
      isNotificationsLoaded: this.isNotificationsLoaded,
      isError: this.isError,
    };
    const props = { ...oldProps, ...newProps };
    return new NotificationModel(props);
  }

  async fetchUnreadCount() {
    const { data } = await client.query<GetUnreadNotificationsCountQuery>({
      query: GetUnreadNotificationsCountDocument,
    });
    return this.copy({ unreadCount: data.notifications.totalCount });
  }

  async fetchNotifications() {
    try {
      const { data } = await client.query<GetNotificationsQuery>({ query: GetNotificationsDocument });
      return this.copy({ notifications: data.notifications, isNotificationsLoaded: true });
    } catch (e) {
      return this.copy({ isError: true });
    }
  }

  async updateAllNotificationsToMarkAsRead() {
    await client.mutate({ mutation: MarkNotificationsAsReadDocument }); // ignore errors because there's no way to handle it
    return this.copy({ unreadCount: 0 });
  }

  async fetchAndMarkAsReadNotifications() {
    const model = await this.fetchNotifications();
    return model.updateAllNotificationsToMarkAsRead();
  }
}
